import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Select, Input, Space, Tag, Modal, Form, Popconfirm, Progress, Dropdown, Menu, message } from "antd";
import { PlusOutlined, SettingOutlined, EditOutlined, DeleteOutlined, MoreOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate, Link } from "react-router-dom";
import moment from "moment";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageHeader from "../../components/ui/PageHeader";
import PageTransition from "../../components/ui/PageTransition";
import EmptyState from "../../components/ui/EmptyState";
import { regionName, regionOptions } from "../../helper/regions";

const ALL = "ALL"; // "applies to everyone" in the region and department filters
const STATUS_STYLE = { published: { color: "green", label: "Published" }, draft: { color: "default", label: "Draft" }, archived: { color: "orange", label: "Archived" } };

const Policies = () => {
  const { request } = useRequest();
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [types, setTypes] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ region: [], department: [], function: [], type: [], status: [] });
  const [typesOpen, setTypesOpen] = useState(false);
  const [typeForm] = Form.useForm();
  const REGIONS = useMemo(() => regionOptions(), []);

  const fetchPolicies = () => {
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    ["region", "department", "function", "type"].forEach((k) => filters[k].length && q.set(k, filters[k].join(",")));
    if (filters.status.length === 1) q.set("status", filters.status[0]); // two statuses are filtered below
    request({ url: `${apiPath.policies}?${q.toString()}`, method: "GET", onSuccess: (res) => setPolicies(res?.data?.data || []) });
  };
  const fetchTypes = () => request({ url: apiPath.policyTypes, method: "GET", onSuccess: (res) => { setTypes(res?.data?.types || []); setFunctions(res?.data?.functions || []); } });

  useEffect(() => {
    fetchTypes();
    request({ url: apiPath.departments, method: "GET", onSuccess: (res) => setDepartments(res?.data?.data || []) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    fetchPolicies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.region, filters.department, filters.function, filters.type, filters.status]);

  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v || [] }));
  const shown = filters.status.length > 1 ? policies.filter((p) => filters.status.includes(p.status)) : policies;
  const multi = { mode: "multiple", allowClear: true, showSearch: true, optionFilterProp: "label", maxTagCount: 0, maxTagPlaceholder: (o) => `${o.length} selected` };

  const FILTER_TITLES = { region: "Region", department: "Department", function: "Function", type: "Type", status: "Status" };
  const labelFor = (key, v) => {
    if (key === "region") return v === ALL ? "All regions" : regionName(v);
    if (key === "department") return v === ALL ? "All departments" : departments.find((d) => d._id === v)?.name || v;
    if (key === "type") return types.find((t) => t._id === v)?.name || v;
    if (key === "status") return STATUS_STYLE[v]?.label || v;
    return v;
  };
  const applied = Object.keys(FILTER_TITLES).flatMap((k) => filters[k].map((v) => ({ k, v })));

  const setStatus = (p, status) =>
    request({
      url: `${apiPath.policies}/${p._id}/status`,
      method: "PUT",
      data: { status },
      onSuccess: () => {
        message.success(`Policy ${status}`);
        fetchPolicies();
      },
      onError: (err) => message.error(err?.response?.data?.message || "Could not change the status"),
    });
  const remove = (p) =>
    request({ url: `${apiPath.policies}/${p._id}`, method: "DELETE", onSuccess: () => { message.success("Policy deleted"); fetchPolicies(); } });

  const addType = () =>
    typeForm.validateFields().then((values) =>
      request({
        url: apiPath.policyTypes,
        method: "POST",
        data: values,
        onSuccess: () => { message.success("Policy type added"); typeForm.resetFields(); fetchTypes(); },
        onError: (err) => message.error(err?.response?.data?.message || "Could not add the type"),
      })
    );
  const removeType = (t) =>
    request({
      url: `${apiPath.policyTypes}/${t._id}`,
      method: "DELETE",
      onSuccess: () => { message.success("Policy type deleted"); fetchTypes(); },
      onError: (err) => message.error(err?.response?.data?.message || "Could not delete the type"),
    });

  const tagList = (items, max = 3, color) => (
    <Space size={[4, 4]} wrap>
      {items.slice(0, max).map((x) => <Tag key={x} color={color} style={{ margin: 0 }}>{x}</Tag>)}
      {items.length > max ? <Tag style={{ margin: 0 }}>+{items.length - max}</Tag> : null}
    </Space>
  );

  const columns = [
    {
      title: "Policy",
      dataIndex: "title",
      width: "30%",
      render: (v, p) => (
        <div>
          <Link to={`/policies/${p._id}`} style={{ fontWeight: 600 }}>{v}</Link>
          <div style={{ color: "#6B7280", fontSize: 12 }}>{p.policyType?.name || "No type"}{p.requiresAcknowledgement ? " · needs acknowledgement" : ""}</div>
        </div>
      ),
    },
    { title: "Function", dataIndex: "function", width: "11%", render: (v) => <Tag color="purple" style={{ margin: 0 }}>{v}</Tag> },
    { title: "Regions", dataIndex: "regions", width: "15%", render: (r) => (r?.length ? tagList(r.map(regionName), 2, "geekblue") : <Tag style={{ margin: 0 }}>All regions</Tag>) },
    { title: "Departments", dataIndex: "departments", width: "15%", render: (d) => (d?.length ? tagList(d.map((x) => x.name), 2, "cyan") : <Tag style={{ margin: 0 }}>All departments</Tag>) },
    { title: "Ver.", dataIndex: "version", width: 60, align: "center", render: (v) => `v${v}` },
    { title: "Status", dataIndex: "status", width: 100, render: (s) => <Tag color={STATUS_STYLE[s]?.color} style={{ margin: 0 }}>{STATUS_STYLE[s]?.label || s}</Tag> },
    {
      title: "Acknowledged",
      dataIndex: "ackProgress",
      width: 130,
      render: (a) => (a ? <div><Progress percent={a.total ? Math.round((a.acknowledged / a.total) * 100) : 0} size="small" showInfo={false} /><div style={{ fontSize: 11, color: "#6B7280" }}>{a.acknowledged} of {a.total}</div></div> : <span style={{ color: "#9CA3AF" }}>—</span>),
    },
    { title: "Updated", dataIndex: "updated_at", width: 105, render: (v) => moment(v).format("DD MMM YYYY") },
    {
      title: "",
      width: 100,
      align: "right",
      render: (_, p) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => navigate(`/policies/${p._id}`)} />
          <Dropdown
            trigger={["click"]}
            overlay={
              <Menu
                items={[
                  p.status !== "published" ? { key: "pub", label: "Publish", onClick: () => setStatus(p, "published") } : { key: "unpub", label: "Move back to draft", onClick: () => setStatus(p, "draft") },
                  p.status !== "archived" ? { key: "arch", label: "Archive", onClick: () => setStatus(p, "archived") } : null,
                ].filter(Boolean)}
              />
            }
          >
            <Button size="small" icon={<MoreOutlined />} />
          </Dropdown>
          <Popconfirm title="Delete this policy?" onConfirm={() => remove(p)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // with functions chosen, only their types are offered
  const typeOptions = types.filter((t) => !filters.function.length || filters.function.includes(t.function)).map((t) => ({ value: t._id, label: filters.function.length === 1 ? t.name : `${t.name} (${t.function})` }));

  return (
    <PageTransition>
      <PageHeader
        title="Policies"
        subtitle="Company policies by region, department and function. Employees see the ones that apply to them."
        actions={
          <Space>
            <Button icon={<SettingOutlined />} onClick={() => setTypesOpen(true)}>Policy types</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/policies/new")}>New policy</Button>
          </Space>
        }
      />
      <Space style={{ marginBottom: 12 }} wrap>
        <Input.Search allowClear prefix={<SearchOutlined />} placeholder="Search policy title" style={{ width: 240 }} value={search} onChange={(e) => setSearch(e.target.value)} onSearch={fetchPolicies} />
        <Select {...multi} placeholder="Region" style={{ width: 190 }} value={filters.region} onChange={set("region")} options={[{ value: ALL, label: "All regions (every employee)" }, ...REGIONS]} />
        <Select {...multi} placeholder="Department" style={{ width: 190 }} value={filters.department} onChange={set("department")} options={[{ value: ALL, label: "All departments" }, ...departments.map((d) => ({ value: d._id, label: d.name }))]} />
        <Select {...multi} placeholder="Function" style={{ width: 170 }} value={filters.function} onChange={(v) => setFilters((f) => ({ ...f, function: v, type: f.type.filter((id) => !v.length || v.includes(types.find((t) => t._id === id)?.function)) }))} options={functions.map((f) => ({ value: f, label: f }))} />
        <Select {...multi} placeholder="Type" style={{ width: 170 }} value={filters.type} onChange={set("type")} options={typeOptions} />
        <Select {...multi} placeholder="Status" style={{ width: 140 }} value={filters.status} onChange={set("status")} options={Object.entries(STATUS_STYLE).map(([value, s]) => ({ value, label: s.label }))} />
      </Space>
      {applied.length ? (
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginBottom: 12 }}>
          <span style={{ color: "#6B7280", fontSize: 12, marginRight: 4 }}>Showing:</span>
          {applied.map(({ k, v }) => (
            <Tag key={`${k}-${v}`} closable onClose={() => setFilters((f) => ({ ...f, [k]: f[k].filter((x) => x !== v) }))} style={{ margin: 0, padding: "3px 10px", borderRadius: 14, background: "#EEF2FF", borderColor: "#C7D2FE", color: "#3730A3" }}>
              {FILTER_TITLES[k]}: <b>{labelFor(k, v)}</b>
            </Tag>
          ))}
          <Button type="link" size="small" onClick={() => setFilters({ region: [], department: [], function: [], type: [], status: [] })}>Clear all</Button>
          <span style={{ color: "#6B7280", fontSize: 12, marginLeft: "auto" }}>{shown.length} policies</span>
        </div>
      ) : null}
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={shown}
        tableLayout="fixed"
        pagination={{ pageSize: 15 }}
        locale={{ emptyText: <EmptyState message="No policies match" actionLabel="New policy" onAction={() => navigate("/policies/new")} /> }}
      />

      <Modal title="Policy types" open={typesOpen} onCancel={() => setTypesOpen(false)} footer={<Button type="primary" onClick={() => setTypesOpen(false)}>Done</Button>} width={640} destroyOnClose>
        <Form layout="inline" form={typeForm} style={{ marginBottom: 14 }}>
          <Form.Item name="name" rules={[{ required: true, message: "Type a name" }]} style={{ flex: 1 }}>
            <Input placeholder="New type, for example Gift & Hospitality Policy" />
          </Form.Item>
          <Form.Item name="function" initialValue="Company-wide">
            <Select style={{ width: 170 }} options={functions.map((f) => ({ value: f, label: f }))} />
          </Form.Item>
          <Button type="primary" icon={<PlusOutlined />} onClick={addType}>Add</Button>
        </Form>
        <div style={{ maxHeight: 380, overflowY: "auto" }}>
          {functions.map((f) => {
            const list = types.filter((t) => t.function === f);
            if (!list.length) return null;
            return (
              <div key={f} style={{ marginBottom: 12 }}>
                <div style={{ fontWeight: 600, fontSize: 12, textTransform: "uppercase", color: "#6B7280", marginBottom: 4 }}>{f}</div>
                <Space size={[6, 6]} wrap>
                  {list.map((t) => (
                    <Tag key={t._id} color={t.isCustom ? "blue" : "default"} closable onClose={(e) => { e.preventDefault(); removeType(t); }} style={{ margin: 0 }}>
                      {t.name}
                    </Tag>
                  ))}
                </Space>
              </div>
            );
          })}
        </div>
        <div style={{ color: "#9CA3AF", fontSize: 12 }}>Click the x on a type to remove it. A type can only be removed once no policy uses it. Types you added are blue.</div>
      </Modal>
    </PageTransition>
  );
};

export default Policies;
